import React, { useState } from "react";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Briefcase, Upload } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Careers = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    position: "",
    message: "",
    resume: null
  });
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, resume: e.target.files[0] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.resume) {
      toast.error("Please upload your resume.");
      return;
    }
    setLoading(true);

    const data = new FormData();
    data.append("name", formData.name);
    data.append("email", formData.email);
    data.append("position", formData.position);
    data.append("message", formData.message);
    data.append("resume", formData.resume);

    try {
      await axios.post(`${API}/careers/`, data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Application submitted successfully!");
      setFormData({ name: "", email: "", position: "", message: "", resume: null });
    } catch (error) {
      console.error(error);
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="bg-slate-900 text-white py-20 relative">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1765994898026-4fa84ade4a61?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxydXJhbCUyMGNvbW11bml0eSUyMGRldmVsb3BtZW50fGVufDB8fHx8MTc3MDQzODUzMXww&ixlib=rb-4.1.0&q=85')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-5xl font-display font-bold uppercase tracking-wide mb-4">Careers</h1>
          <p className="text-xl text-gray-300 max-w-2xl font-light">
            Build the future of heavy industry with us.
          </p>
        </div>
      </div>

      <main className="flex-grow py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Open Positions Mock */}
            <div>
              <h2 className="text-3xl font-display font-bold text-foreground mb-8">Current Openings</h2>
              <div className="space-y-6">
                {[
                  { title: "Senior Mining Engineer", loc: "Jharkhand", type: "Full-time" },
                  { title: "Safety Officer", loc: "Odisha", type: "On-site" },
                  { title: "Mechanical Maintenance Lead", loc: "Chhattisgarh", type: "Full-time" }
                ].map((job, idx) => (
                  <div key={idx} className="bg-white p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-primary mb-1">{job.title}</h3>
                        <p className="text-gray-500 text-sm">{job.loc} • {job.type}</p>
                      </div>
                      <div className="bg-primary/10 p-2 rounded-full">
                        <Briefcase className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <Button variant="outline" className="w-full uppercase text-xs tracking-wider">View Details</Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Application Form */}
            <div className="bg-white p-8 border-t-4 border-primary shadow-lg">
               <h2 className="text-2xl font-display font-bold text-foreground mb-2">Join Our Team</h2>
               <p className="text-gray-500 mb-8 text-sm">Submit your CV for general consideration or specific roles.</p>
               
               <form onSubmit={handleSubmit} className="space-y-6">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                   <div className="space-y-2">
                     <Label htmlFor="name">Full Name</Label>
                     <Input 
                        id="name" 
                        placeholder="John Doe" 
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        className="rounded-none focus-visible:ring-primary"
                     />
                   </div>
                   <div className="space-y-2">
                     <Label htmlFor="email">Email Address</Label>
                     <Input 
                        id="email" 
                        type="email" 
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required 
                        className="rounded-none focus-visible:ring-primary"
                     />
                   </div>
                 </div>
                 
                 <div className="space-y-2">
                   <Label htmlFor="position">Position Applied For</Label>
                   <Input 
                      id="position" 
                      placeholder="e.g. Mining Engineer"
                      value={formData.position}
                      onChange={(e) => setFormData({...formData, position: e.target.value})}
                      className="rounded-none focus-visible:ring-primary"
                   />
                 </div>

                 <div className="space-y-2">
                   <Label htmlFor="resume">Resume / CV</Label>
                   <div className="flex items-center justify-center w-full">
                      <label htmlFor="resume-upload" className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <Upload className="w-8 h-8 mb-4 text-gray-500" />
                              <p className="text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                              <p className="text-xs text-gray-500">PDF, DOC (MAX. 5MB)</p>
                          </div>
                          <input id="resume-upload" type="file" className="hidden" onChange={handleFileChange} accept=".pdf,.doc,.docx" />
                      </label>
                   </div>
                   {formData.resume && <p className="text-sm text-green-600 mt-2">Selected: {formData.resume.name}</p>}
                 </div>

                 <div className="space-y-2">
                   <Label htmlFor="message">Cover Letter / Message</Label>
                   <Textarea 
                      id="message" 
                      placeholder="Tell us why you are a good fit..."
                      className="min-h-[100px] rounded-none focus-visible:ring-primary"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                   />
                 </div>

                 <Button type="submit" size="lg" disabled={loading} className="w-full bg-primary hover:bg-primary/90 text-white rounded-none uppercase tracking-widest">
                   {loading ? "Submitting..." : "Submit Application"}
                 </Button>
               </form>
            </div>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Careers;
